import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AuthShell, ErrorNote } from "./Login";

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const clientError = useMemo(() => {
    if (!token) return "This reset link is missing its token. Please request a new one.";
    if (pw && pw.length < 8) return "Password must be at least 8 characters.";
    if (pw && pw2 && pw !== pw2) return "Passwords don't match.";
    return "";
  }, [token, pw, pw2]);

  const submit = async (e) => {
    e.preventDefault();
    if (clientError) { setError(clientError); return; }
    setBusy(true);
    setError("");
    try {
      await api.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell
        title="Password updated"
        subtitle="You can now sign in with your new password."
        footer={<Link to="/login" className="font-bold text-[#0D9488] hover:underline" data-testid="reset-back-to-login">Go to log in</Link>}
      >
        <div className="rounded-2xl border-2 border-[#10B981]/40 bg-[#10B981]/10 px-4 py-4 text-sm font-semibold text-[#047857]" data-testid="reset-done-message">
          <div className="flex items-center gap-2 font-heading text-base font-bold"><CheckCircle2 size={18} /> All set</div>
          <p className="mt-1 font-body text-sm text-[#047857]/90">Redirecting you to the login page…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Must be at least 8 characters. Pick something you'll remember."
      footer={<Link to="/login" className="font-bold text-[#0D9488] hover:underline" data-testid="reset-back-to-login">Back to log in</Link>}
    >
      <form onSubmit={submit}>
        <ErrorNote message={error || (clientError && (pw || pw2) ? clientError : "")} />
        <label className="block text-sm font-bold text-[#1F2937] mb-2">New password</label>
        <div className="relative">
          <KeyRound size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            data-testid="reset-password-input"
            type={show ? "text" : "password"}
            required
            minLength={8}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className={`${inputClass} pl-11 pr-11`}
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid="reset-toggle-visibility"
            aria-label="Toggle password visibility"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0D9488]">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Confirm password</label>
        <input
          data-testid="reset-confirm-input"
          type={show ? "text" : "password"}
          required
          minLength={8}
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          className={inputClass}
          placeholder="Repeat your new password"
          autoComplete="new-password"
        />
        <button type="submit" disabled={busy || !pw || !pw2 || !!clientError} data-testid="reset-submit-button"
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
          {busy ? "Updating..." : "Update password"} <ArrowRight size={20} />
        </button>
      </form>
    </AuthShell>
  );
}
